import { useEffect, useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { LogOut, User, X, Menu, Newspaper, ShoppingBag, Users } from 'lucide-react'
import PageTransition from './PageTransition'
// BannerDisplay removed for between-sections banners
import InstallPWAButton from './InstallPWAButton'
import WiserrosWidgetSimple from './WiserrosWidgetSimple'
import SearchBar from './SearchBar'
import MobileBottomNav from './MobileBottomNav'
import NotificationBadge from './NotificationBadge'
import useSound from '../hooks/useSound'
import { useBanners } from '../hooks/useBanners'
import BannerDisplay from './BannerDisplay'
import OptimizedImage from './OptimizedImage'

// Helper function to get week number (outside component to avoid dependency issues)
const getWeekNumber = (date: Date): number => {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
  const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
}

const Layout = () => {

  const { user, logout, isAdmin, isModerator } = useAuth()
  const { counts, markMessagesRead, markNewsRead, markGroupsRead, markMarketplaceRead } = useNotifications()
  const location = useLocation()
  const navigate = useNavigate()
  const isAdminPage = location.pathname.startsWith('/admin')
  const isHomePage = location.pathname === '/'
  
  // Redirect to email verification page if user is logged in but email is not verified
  useEffect(() => {
    if (user && !user.emailVerified) {
      const allowedPaths = ['/login', '/register', '/verify-email-required']
      if (!allowedPaths.includes(location.pathname)) {
        navigate('/verify-email-required', { replace: true })
      }
    }
  }, [user, location.pathname, navigate])
  
  type SidebarSide = 'left' | 'right' | null
  const [singleSidebar, setSingleSidebar] = useState<SidebarSide>(null)
  useEffect(() => {
    const p = location.pathname
    if (p.startsWith('/news')) setSingleSidebar('left')
    else setSingleSidebar(null)
  }, [location.pathname])
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)
  const [sidebarLeftVisible, setSidebarLeftVisible] = useState(true)
  const [sidebarRightVisible, setSidebarRightVisible] = useState(true)
  const closeMobileMenu = () => setMobileMenuOpen(false)
  const closeProfileDrawer = () => setProfileDrawerOpen(false)

  // Reset sidebar visibility when entering groups page
  useEffect(() => {
    const p = location.pathname
    if (p.startsWith('/groups')) {
      setSidebarLeftVisible(true)
      setSidebarRightVisible(true)
    }
  }, [location.pathname])
  // Banner visibility flags removed (no between-sections banners)
  // const [bannerVisibility, setBannerVisibility] = useState({
  //   sidebarLeft: true,
  //   sidebarRight: true,
  //   popup: true,
  // })
   const [popupClosed, setPopupClosed] = useState(false)
   const [headerTopClosed, setHeaderTopClosed] = useState(false)
   const [footerBottomClosed, setFooterBottomClosed] = useState(false)

   // Banner data for each position
   const sidebarLeft = useBanners({ position: 'sidebar-left', active: true })
   const sidebarRight = useBanners({ position: 'sidebar-right', active: true })
   const headerTopBanners = useBanners({ position: 'header-top', active: true })
   const footerBottomBanners = useBanners({ position: 'footer-bottom', active: true })
   const popupBanners = useBanners({ position: 'popup', active: true, limit: 1 })

   // Sound effects
  const playClick = useSound('click')
  const playNavigation = useSound('navigation')

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  // toggleBanner removed (no between-sections banner)

     const closePopup = () => {
       setPopupClosed(true)
       // Store the current week number when popup is closed
       const currentWeek = getWeekNumber(new Date())
       localStorage.setItem('noe_popup_last_shown_week', currentWeek.toString())
     }

    const closeHeaderTop = () => {
      console.log('Closing header top banner')
      setHeaderTopClosed(true)
    }

      const closeFooterBottom = () => {
        console.log('Closing footer bottom banner')
        setFooterBottomClosed(true)
      }

      const closeSidebarLeft = () => {
        console.log('Closing sidebar left')
        setSidebarLeftVisible(false)
      }

       const closeSidebarRight = () => {
         console.log('Closing sidebar right')
         setSidebarRightVisible(false)
       }



   useEffect(() => {
     // Notify splash screen that the app layout has mounted
     if (typeof window !== 'undefined' && (window as unknown as { __dismissSplash?: () => void }).__dismissSplash) {
       (window as unknown as { __dismissSplash?: () => void }).__dismissSplash!()
     }
   }, [])

   useEffect(() => {
     // Check if popup has been shown this week
     const lastShownWeek = localStorage.getItem('noe_popup_last_shown_week')
     const currentWeek = getWeekNumber(new Date())
     
     if (lastShownWeek && parseInt(lastShownWeek) === currentWeek) {
       // Popup already shown this week, keep it closed
       setPopupClosed(true)
     } else {
       // New week or never shown, show popup
       setPopupClosed(false)
     }
   }, [])

   // showFavoritesNav will be controlled by gating logic added later

  return (
    <div className="flex flex-col min-h-screen bg-noe-950 text-noe-100 transition-colors duration-500 overflow-x-hidden">
       {/* Top banner moved below header */}

       {/* Header */}
      <header className="hidden md:block sticky top-0 z-50 border-b border-noe-800 backdrop-blur-lg bg-noe-950/80">
        {/* Top row: Logo, Search (desktop), Menu button */}
         <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="w-2 h-2 rounded-full bg-noe-brand animate-pulse" />
                <span className="text-xl font-bold tracking-wider">NOÉ CONNECT</span>
            </Link>
          </div>
          
          {/* Center: Search bar (hidden on mobile) */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-4 relative">
            <SearchBar compact />
          </div>
          
          {/* Right: Desktop nav + mobile menu button */}
          <div className="flex items-center gap-4">
            <nav className="hidden md:flex items-center gap-6">
                 <Link to="/" onClick={playNavigation} className={`text-base hover:text-noe-brand transition-colors ${isActive('/') ? 'text-noe-brand' : 'text-noe-300'}`}>
                  Início
                </Link>
                 <Link to="/news" onClick={() => { playNavigation(); markNewsRead(); }} className={`text-base hover:text-noe-brand transition-colors ${isActive('/news') ? 'text-noe-brand' : 'text-noe-300'}`}>
                  Notícias
                  {counts.news > 0 && <NotificationBadge count={counts.news} className="ml-2" />}
                </Link>
                 <Link to="/listings" onClick={() => { playNavigation(); markMarketplaceRead(); }} className={`text-base hover:text-noe-brand transition-colors ${isActive('/listings') ? 'text-noe-brand' : 'text-noe-300'}`}>
                 <div className="relative inline-block">
                   Marketplace
                   {counts.marketplace > 0 && <NotificationBadge count={counts.marketplace} className="ml-2" />}
                 </div>
               </Link>
                <Link to="/groups" onClick={() => { playNavigation(); markGroupsRead(); }} className={`text-base hover:text-noe-brand transition-colors ${isActive('/groups') ? 'text-noe-brand' : 'text-noe-300'}`}>
                 <div className="relative inline-block">
                   Grupos
                   {counts.groups > 0 && <NotificationBadge count={counts.groups} className="ml-2" />}
                 </div>
               </Link>
              {user && (
                  <Link to="/my-listings" onClick={() => { playNavigation(); markMarketplaceRead(); }} className={`text-base hover:text-noe-brand transition-colors ${isActive('/my-listings') ? 'text-noe-brand' : 'text-noe-300'}`}>
                   Meus Anúncios
                 </Link>
              )}
              {user && (
                <Link to="/messages" onClick={() => { playNavigation(); markMessagesRead(); }} className={`text-base hover:text-noe-brand transition-colors ${isActive('/messages') ? 'text-noe-brand' : 'text-noe-300'}`}>
                 Mensagens
                 {counts.messages > 0 && <NotificationBadge count={counts.messages} className="ml-2" />}
               </Link>
              )}
              {user && (isAdmin || isModerator) && (
                  <Link to="/team" onClick={playNavigation} className={`text-base hover:text-noe-brand transition-colors ${isActive('/team') ? 'text-noe-brand' : 'text-noe-300'}`}>
                   Time
                 </Link>
              )}
              {user && (isAdmin || isModerator) && (
                  <Link to="/admin" onClick={playNavigation} className={`text-base hover:text-noe-brand transition-colors flex items-center gap-1 ${isActive('/admin') ? 'text-noe-brand' : 'text-noe-300'}`}>
                   <span className="px-2 py-1 text-xs bg-noe-brand/20 text-noe-brand rounded">Admin</span>
                 </Link>
              )}
              {user ? (
                <>
                      <Link to="/profile" onClick={() => { playNavigation(); }} className={`flex items-center gap-2 text-base hover:text-noe-brand transition-colors ${isActive('/profile') ? 'text-noe-brand' : ''}`}>
                       <User className="w-4 h-4 text-noe-brand" />
                       <span className={isActive('/profile') ? 'text-noe-brand' : 'text-noe-300'}>{user.displayName || 'Grupo Noé'}</span>
                     </Link>
                   <button
                     onClick={() => { playClick(); logout(); }}
                      className="text-base text-noe-300 hover:text-noe-brand transition-colors flex items-center gap-1"
                   >
                     <LogOut className="w-4 h-4" />
                     Sair
                   </button>
                </>
              ) : (
                  <Link to="/login" onClick={playNavigation} className={`text-base hover:text-noe-brand transition-colors ${isActive('/login') ? 'text-noe-brand' : 'text-noe-300'}`}>
                   Login
                 </Link>
              )}
              {/* Favoritos badge in header: show only when there are message notifications (per current policy) */}
              {counts.messages > 0 && (
                <NotificationBadge count={counts.messages} className="ml-2" />
              )}
            </nav>
            
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-noe-300 hover:text-noe-brand transition-colors"
              onClick={() => { playClick(); setMobileMenuOpen(!mobileMenuOpen); }}
              aria-label="Abrir menu"
            >
              <Menu className="w-6 h-6" />
            </button>
           </div>
         </div>
         
         {/* Bottom row: Search bar (mobile only) */}
          <div className="md:hidden px-4 sm:px-6 pb-2">
           <SearchBar compact />
         </div>

        {/* Mobile menu panel */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-noe-900 border-t border-noe-800 overflow-hidden"
            >
               <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-4">
                     <Link to="/" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`text-base hover:text-noe-brand transition-colors py-2 ${isActive('/') ? 'text-noe-brand' : 'text-noe-300'}`}>
                      Início
                    </Link>
                     <Link to="/news" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`text-base hover:text-noe-brand transition-colors py-2 ${isActive('/news') ? 'text-noe-brand' : 'text-noe-300'}`}>
                      Notícias
                    </Link>
                     <Link to="/listings" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`text-base hover:text-noe-brand transition-colors py-2 ${isActive('/listings') ? 'text-noe-brand' : 'text-noe-300'}`}>
                     Marketplace
                   </Link>
                     <Link to="/groups" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`text-base hover:text-noe-brand transition-colors py-2 ${isActive('/groups') ? 'text-noe-brand' : 'text-noe-300'}`}>
                     Grupos
                   </Link>
                 {user && (
                      <Link to="/my-listings" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`text-base hover:text-noe-brand transition-colors py-2 ${isActive('/my-listings') ? 'text-noe-brand' : 'text-noe-300'}`}>
                      Meus Anúncios
                    </Link>
                 )}
                 {user && (
                      <Link to="/messages" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`text-base hover:text-noe-brand transition-colors py-2 ${isActive('/messages') ? 'text-noe-brand' : 'text-noe-300'}`}>
                      Mensagens
                    </Link>
                 )}
                  {user && (isAdmin || isModerator) && (
                      <Link to="/team" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`text-base hover:text-noe-brand transition-colors py-2 ${isActive('/team') ? 'text-noe-brand' : 'text-noe-300'}`}>
                       Time
                     </Link>
                  )}
                  {user && (isAdmin || isModerator) && (
                      <Link to="/admin" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`text-base hover:text-noe-brand transition-colors py-2 flex items-center gap-1 ${isActive('/admin') ? 'text-noe-brand' : 'text-noe-300'}`}>
                       <span className="px-2 py-1 text-xs bg-noe-brand/20 text-noe-brand rounded">Admin</span>
                     </Link>
                  )}
                {user ? (
                  <>
                        <Link to="/profile" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`flex items-center gap-2 text-lg hover:text-noe-brand transition-colors py-2 ${isActive('/profile') ? 'text-noe-brand' : 'text-noe-300'}`}>
                        <User className="w-4 h-4 text-noe-brand" />
                        <span>{user.displayName || 'Grupo Noé'}</span>
                      </Link>
                     <button
                        onClick={() => { playClick(); logout(); closeMobileMenu(); }}
                        className="text-lg text-noe-300 hover:text-noe-brand transition-colors flex items-center gap-1 py-2"
                     >
                       <LogOut className="w-4 h-4" />
                       Sair
                     </button>
                  </>
                ) : (
                      <Link to="/login" onClick={() => { playNavigation(); closeMobileMenu(); }} className={`text-lg hover:text-noe-brand transition-colors py-2 ${isActive('/login') ? 'text-noe-brand' : 'text-noe-300'}`}>
                      Login
                    </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
       </header>

         {/* Header top banner */}

            {(headerTopBanners.loading || headerTopBanners.banners.length > 0) && !headerTopClosed && !isAdminPage && !isHomePage && (
            <div className="hidden md:block">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 bg-noe-900/30 border-b border-noe-800 relative">
                  <button
                    onClick={() => { playClick(); closeHeaderTop(); }}
                    className="absolute right-2 top-2 p-1 bg-noe-800/50 rounded-full hover:bg-noe-700/50 transition-colors z-10"
                    title="Esconder banner"
                  >
                    <X className="w-3 h-3" />
                  </button>
                 <BannerDisplay position="header-top" banners={headerTopBanners.banners} />
               </div>
            </div>
          )}

       {/* Main content */}
      <main className="flex-grow w-full flex">
        {/* Sidebar left for banners (single-sidebar mode supported) */}
        <AnimatePresence>

                {(sidebarLeft.loading || sidebarLeft.banners.length > 0) && (singleSidebar === null || singleSidebar === 'left') && sidebarLeftVisible && !isAdminPage && !isHomePage && (
              <motion.aside
                initial={{ opacity: 1, width: '16rem' }}
                animate={{ opacity: 1, width: '16rem' }}
                exit={{ opacity: 0, width: 0, paddingLeft: 0, paddingRight: 0, borderRightWidth: 0 }}
                transition={{ duration: 0.3 }}
                className="hidden lg:block w-64 xl:w-80 px-6 py-8 border-r border-noe-800 overflow-hidden"
              >
                <div className="relative">
                    <button
                       onClick={() => { playClick(); closeSidebarLeft(); }}
                      className="absolute -right-2 top-0 p-1 bg-noe-800/50 rounded-full hover:bg-noe-700/50 transition-colors z-10"
                     title="Esconder banner"
                   >
                     <X className="w-3 h-3" />
                   </button>
                    <BannerDisplay position="sidebar-left" banners={sidebarLeft.banners} />
                </div>
              </motion.aside>
            )}
          </AnimatePresence>

        {/* Central content */}
        <div className={`flex-1 w-full mx-auto ${location.pathname === '/reels' ? 'p-0' : 'px-4 sm:px-6 py-4 sm:py-6'} flex flex-col items-center md:items-start`}>
          <PageTransition>
            <Outlet />
          </PageTransition>
        </div>

        {/* Sidebar right for banners (respect singleSidebar gating) */}
        <AnimatePresence>

              {(sidebarRight.loading || sidebarRight.banners.length > 0) && (singleSidebar === null || singleSidebar === 'right') && sidebarRightVisible && !isAdminPage && !isHomePage && (
              <motion.aside
                initial={{ opacity: 1, width: '16rem' }}
                animate={{ opacity: 1, width: '16rem' }}
                exit={{ opacity: 0, width: 0, paddingLeft: 0, paddingRight: 0, borderLeftWidth: 0 }}
                transition={{ duration: 0.3 }}
                className="hidden lg:block w-64 xl:w-80 px-6 py-8 border-l border-noe-800 overflow-hidden"
              >
                <div className="relative">
                    <button
                       onClick={() => { playClick(); closeSidebarRight(); }}
                      className="absolute -left-2 top-0 p-1 bg-noe-800/50 rounded-full hover:bg-noe-700/50 transition-colors z-10"
                    title="Esconder banner"
                  >
                    <X className="w-3 h-3" />
                  </button>
                    <BannerDisplay position="sidebar-right" banners={sidebarRight.banners} />
                </div>
              </motion.aside>
            )}
          </AnimatePresence>
      </main>

      {/* Banner entre páginas removido (between-sections) */}



         {/* Footer banner */}

            {(footerBottomBanners.loading || footerBottomBanners.banners.length > 0) && !footerBottomClosed && !isAdminPage && !isHomePage && (
           <div className="hidden md:block">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 bg-noe-900/30 border-t border-noe-800 overflow-hidden relative">
                <button
                  onClick={() => { playClick(); closeFooterBottom(); }}
                  className="absolute -right-2 -top-2 p-1 bg-noe-800/50 rounded-full hover:bg-noe-700/50 transition-colors z-10"
                  title="Esconder banner"
                >
                  <X className="w-3 h-3" />
                </button>
               <BannerDisplay position="footer-bottom" banners={footerBottomBanners.banners} />
             </div>
           </div>
         )}

       {/* Footer - hidden on reels page */}
       {location.pathname !== '/reels' && (
          <footer className="mt-auto border-t border-noe-800 py-6">
           <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-noe-500 text-sm">
             <p>Noé Connect • Marketplace do Grupo Noé para brasileiros nos EUA</p>
             <p className="mt-2 text-xs">Conectando imóveis, carros, serviços e oportunidades</p>
           </div>
         </footer>
       )}

      {/* Mobile Bottom Nav */}
      <MobileBottomNav isActive={isActive} onProfileClick={() => setProfileDrawerOpen(!profileDrawerOpen)} />

      {/* Profile Drawer */}
      <AnimatePresence>
        {profileDrawerOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[60] bg-noe-900 border-l border-noe-800 p-6 flex flex-col gap-4"
          >
             <button onClick={closeProfileDrawer} className="self-end p-2">
                <X className="w-6 h-6 text-noe-300" />
             </button>
             
             <div className="flex flex-col gap-6 mt-10">
                {user ? (
                  <>
                    <div className="flex items-center gap-4 p-4 bg-noe-950 rounded-xl border border-noe-800">
                       {user.photoURL ? (
                         <OptimizedImage
                           src={user.photoURL}
                           alt={user.displayName || 'Usuário'}
                           className="w-16 h-16 rounded-full object-cover border-2 border-noe-brand"
                           width={64}
                           height={64}
                           fallbackSrc={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'U')}&background=bfa757&color=000000`}
                         />
                       ) : (
                         <div className="w-16 h-16 rounded-full bg-noe-800 flex items-center justify-center text-2xl font-bold text-noe-brand">
                           {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
                         </div>
                       )}
                      <div className="flex flex-col">
                        <span className="font-bold text-lg">{user.displayName || 'Usuário'}</span>
                        <span className="text-sm text-noe-400">Ver Perfil</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                        <Link to="/profile" onClick={() => { playNavigation(); closeProfileDrawer(); }} className="flex items-center gap-4 text-lg p-3 hover:bg-noe-800 rounded-lg transition-colors">
                        <User className="w-6 h-6 text-noe-brand" />
                        <span>Meu Perfil</span>
                        </Link>
                        <Link to="/my-listings" onClick={() => { playNavigation(); closeProfileDrawer(); }} className="flex items-center gap-4 text-lg p-3 hover:bg-noe-800 rounded-lg transition-colors">
                        <ShoppingBag className="w-6 h-6 text-noe-brand" />
                        <span>Meus Anúncios</span>
                        </Link>
                        <Link to="/messages" onClick={() => { playNavigation(); closeProfileDrawer(); }} className="flex items-center gap-4 text-lg p-3 hover:bg-noe-800 rounded-lg transition-colors">
                        <Newspaper className="w-6 h-6 text-noe-brand" />
                        <span>Mensagens</span>
                        </Link>
                        {(isAdmin || isModerator) && (
                          <>
                            <Link to="/team" onClick={() => { playNavigation(); closeProfileDrawer(); }} className="flex items-center gap-4 text-lg p-3 hover:bg-noe-800 rounded-lg transition-colors">
                              <Users className="w-6 h-6 text-noe-brand" />
                              <span>Time</span>
                            </Link>
                            <Link to="/admin" onClick={() => { playNavigation(); closeProfileDrawer(); }} className="flex items-center gap-4 text-lg p-3 hover:bg-noe-800 rounded-lg transition-colors">
                              <span className="px-2 py-1 text-xs bg-noe-brand/20 text-noe-brand rounded">Admin</span>
                              <span>Painel Admin</span>
                            </Link>
                          </>
                        )}
                        <button
                        onClick={() => { playClick(); logout(); closeProfileDrawer(); }}
                        className="flex items-center gap-4 text-lg p-3 text-noe-300 hover:text-red-500 hover:bg-noe-800 rounded-lg transition-colors"
                        >

                        <LogOut className="w-6 h-6" />
                        Sair
                        </button>
                    </div>
                  </>
                ) : (
                  <Link to="/login" onClick={() => { playNavigation(); closeProfileDrawer(); }} className="flex items-center gap-4 text-lg p-3 hover:bg-noe-800 rounded-lg transition-colors">
                    <User className="w-6 h-6 text-noe-brand" />
                    <span>Login</span>
                  </Link>
                )}
             </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popup Modal */}
       <AnimatePresence>
           {!popupClosed && (popupBanners.loading || popupBanners.banners.length > 0) && !isAdminPage && !isHomePage && (
           <motion.div
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             exit={{ opacity: 0 }}
             transition={{ duration: 0.3 }}
             className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
             onClick={closePopup}
           >
             <motion.div
               initial={{ scale: 0.9, opacity: 0 }}
               animate={{ scale: 1, opacity: 1 }}
               exit={{ scale: 0.9, opacity: 0 }}
               transition={{ duration: 0.3 }}
               className="relative max-w-lg w-full bg-noe-900 rounded-2xl border border-noe-800 overflow-hidden"
               onClick={(e) => e.stopPropagation()}
             >
               <button
                 onClick={() => { playClick(); closePopup(); }}
                 className="absolute right-4 top-4 p-2 bg-noe-800/50 rounded-full hover:bg-noe-700/50 transition-colors z-10"
                 aria-label="Fechar"
               >
                 <X className="w-4 h-4" />
               </button>
               <div className="p-6">
                 <BannerDisplay position="popup" limit={1} banners={popupBanners.banners} />
                 <div className="mt-4 text-center">
                   <button
                     onClick={() => { playClick(); closePopup(); }}
                     className="px-6 py-2 bg-noe-brand text-noe-950 font-medium rounded-lg hover:bg-noe-brand/90 transition-colors"
                   >
                     Entendi
                   </button>
                 </div>
               </div>
             </motion.div>
           </motion.div>
         )}
        </AnimatePresence>

      <InstallPWAButton />
      {!isAdminPage && <WiserrosWidgetSimple />}
    </div>
  )
}

export default Layout
